import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import {
  Card,
  Descriptions,
  Tag,
  Avatar,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Popconfirm,
  message,
  Table,
  Image,
  Modal,
  Input,
} from 'antd';
import { UserOutlined, CheckOutlined, CloseOutlined, StopOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { apiClient } from '../../lib/api-client';
import { DriverLocationMap } from '../../components/maps';

interface Vehicle {
  id: number;
  make: string;
  model: string;
  year: number;
  plate: string;
  color: string;
  seats: number;
  isSelected: boolean;
}

interface Document {
  id: number;
  type: string;
  fileUrl: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
}

interface Driver {
  id: number;
  fullName: string | null;
  phone: string;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED' | 'RESTING';
  photoUrl: string | null;
  rating: number;
  totalTrips: number;
  walletBalance: number;
  isDriverVerified: boolean;
  createdAt: string;
  vehicles: Vehicle[];
  documents: Document[];
  currentLat?: number;
  currentLong?: number;
  locationUpdatedAt?: string;
}

export const DriverShow = () => {
  const { queryResult } = useShow<Driver>();
  const { data, isLoading, refetch } = queryResult;
  const driver = data?.data;

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const handleVerify = async () => {
    try {
      await apiClient.post(`/admin/drivers/${driver?.id}/verify`);
      message.success('Haydovchi tasdiqlandi');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.error('Sabab kiritilishi shart');
      return;
    }

    setRejecting(true);
    try {
      await apiClient.post(`/admin/drivers/${driver?.id}/reject`, {
        reason: rejectReason,
      });
      message.success('Haydovchi rad etildi');
      setRejectModalOpen(false);
      setRejectReason('');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    } finally {
      setRejecting(false);
    }
  };

  const handleBlock = async () => {
    try {
      await apiClient.post(`/admin/users/${driver?.id}/block`, {
        reason: 'Admin tomonidan bloklangan',
      });
      message.success('Haydovchi bloklandi');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleUnblock = async () => {
    try {
      await apiClient.post(`/admin/users/${driver?.id}/unblock`);
      message.success('Haydovchi blokdan chiqarildi');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
  };

  const documentTypeLabels: Record<string, string> = {
    DRIVER_LICENSE_FRONT: 'Haydovchilik guvohnomasi',
    DRIVER_SELFIE: 'Selfie',
    VEHICLE_REGISTRATION: 'Texnik passport',
    VEHICLE_PHOTO_FRONT: 'Avtomobil (old)',
    VEHICLE_PHOTO_BACK: 'Avtomobil (orqa)',
    VEHICLE_PHOTO_LEFT: 'Avtomobil (chap)',
    VEHICLE_PHOTO_RIGHT: 'Avtomobil (o\'ng)',
    VEHICLE_PHOTO_INTERIOR: 'Avtomobil ichki',
  };

  const vehicleColumns = [
    { title: 'Marka', dataIndex: 'make', key: 'make' },
    { title: 'Model', dataIndex: 'model', key: 'model' },
    { title: 'Yil', dataIndex: 'year', key: 'year' },
    { title: 'Raqam', dataIndex: 'plate', key: 'plate' },
    { title: 'Rang', dataIndex: 'color', key: 'color' },
    {
      title: 'Faol',
      dataIndex: 'isSelected',
      key: 'isSelected',
      render: (selected: boolean) =>
        selected ? <Tag color="green">Ha</Tag> : <Tag>Yo'q</Tag>,
    },
  ];

  return (
    <>
      <Show
        isLoading={isLoading}
        title="Haydovchi"
        headerButtons={
          <Space>
            {!driver?.isDriverVerified && (
              <>
                <Popconfirm
                  title="Haydovchini tasdiqlaysizmi?"
                  onConfirm={handleVerify}
                >
                  <Button type="primary" icon={<CheckOutlined />}>
                    Tasdiqlash
                  </Button>
                </Popconfirm>
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => setRejectModalOpen(true)}
                >
                  Rad etish
                </Button>
              </>
            )}
            {driver?.status === 'BLOCKED' ? (
              <Popconfirm
                title="Blokdan chiqarishni tasdiqlaysizmi?"
                onConfirm={handleUnblock}
              >
                <Button type="primary" icon={<CheckOutlined />}>
                  Blokdan chiqarish
                </Button>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Haydovchini bloklaysizmi?"
                onConfirm={handleBlock}
              >
                <Button danger icon={<StopOutlined />}>
                  Bloklash
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <Card>
              <div className="text-center">
                <Avatar
                  size={100}
                  src={driver?.photoUrl}
                  icon={<UserOutlined />}
                  style={{ marginBottom: 16 }}
                />
                <h3>{driver?.fullName || 'Ismi yo\'q'}</h3>
                <p style={{ color: '#7F8C8D' }}>{driver?.phone}</p>
                <Space>
                  {driver?.isDriverVerified ? (
                    <Tag color="green" icon={<CheckOutlined />}>
                      Tasdiqlangan
                    </Tag>
                  ) : (
                    <Tag color="orange" icon={<CloseOutlined />}>
                      Tasdiqlanmagan
                    </Tag>
                  )}
                  <Tag
                    color={
                      driver?.status === 'ONLINE'
                        ? 'green'
                        : driver?.status === 'BLOCKED'
                          ? 'red'
                          : 'default'
                    }
                  >
                    {driver?.status}
                  </Tag>
                </Space>
              </div>
            </Card>

            <Card title="Statistika" style={{ marginTop: 24 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic title="Reyting" value={driver?.rating != null ? Number(driver.rating).toFixed(1) : '-'} />
                </Col>
                <Col span={12}>
                  <Statistic title="Sayohatlar" value={driver?.totalTrips || 0} />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Statistic
                    title="Hamyon"
                    value={formatCurrency(driver?.walletBalance || 0)}
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card title="Avtomobillar" style={{ marginBottom: 24 }}>
              <Table
                dataSource={driver?.vehicles || []}
                columns={vehicleColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </Card>

            <Card title="Hujjatlar" style={{ marginBottom: 24 }}>
              <Row gutter={[16, 16]}>
                {driver?.documents?.map((doc) => (
                  <Col xs={24} sm={12} md={8} key={doc.id}>
                    <Card
                      size="small"
                      title={documentTypeLabels[doc.type] || doc.type}
                      extra={
                        <Tag
                          color={
                            doc.status === 'APPROVED'
                              ? 'green'
                              : doc.status === 'REJECTED'
                                ? 'red'
                                : 'orange'
                          }
                        >
                          {doc.status === 'APPROVED'
                            ? 'Tasdiqlangan'
                            : doc.status === 'REJECTED'
                              ? 'Rad etilgan'
                              : 'Kutilmoqda'}
                        </Tag>
                      }
                    >
                      <Image
                        src={doc.fileUrl}
                        alt={doc.type}
                        style={{ width: '100%', height: 150, objectFit: 'cover' }}
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* Driver Location Map */}
            <DriverLocationMap
              location={
                driver?.currentLat && driver?.currentLong
                  ? {
                      lat: driver.currentLat,
                      lng: driver.currentLong,
                      updatedAt: driver.locationUpdatedAt,
                      status: driver.status,
                    }
                  : null
              }
              driverName={driver?.fullName || 'Haydovchi'}
              isLoading={isLoading}
              height={350}
            />
          </Col>
        </Row>
      </Show>

      {/* Reject Modal */}
      <Modal
        title="Haydovchini rad etish"
        open={rejectModalOpen}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectReason('');
        }}
        onOk={handleReject}
        okText="Rad etish"
        cancelText="Bekor qilish"
        okButtonProps={{ danger: true, loading: rejecting }}
      >
        <p style={{ marginBottom: 16 }}>
          Haydovchini rad etish sababini kiriting:
        </p>
        <Input.TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Rad etish sababi..."
          rows={4}
        />
      </Modal>
    </>
  );
};
